import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PhoneIncoming, PhoneOutgoing, Mic, RefreshCw, X, Calendar } from 'lucide-react';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { marketingService } from '../../../services/api';
import MaskedMobile from '../../common/MaskedMobile';
import TableSkeleton from '../../common/TableSkeleton';
import DateRangePicker from '../../common/DateRangePicker';
import Pagination from '../../common/Pagination';
import Select from '../../common/Select';
import { StatCardRow } from '../../common/StatCard';
import { formatDateTimeDdmmyyyy } from '../../../utils/format';
import { personName, staffFilterOption, staffFilterValue } from '../../common/StaffFilterOption';

interface CallRow {
  call_id: string;
  phone_number: string;
  contact_name: string | null;
  call_type: 'INCOMING' | 'OUTGOING' | string;
  started_at: string;
  duration_seconds: number;
  connected_seconds: number;
  answered: boolean;
  has_recording: boolean;
  recording_url: string | null;
  caller_mobile: string;
  caller_name: string;
  caller_role: string;
  lead_id: string | null;
  lead_name: string | null;
}

interface MarketingStaff {
  mobile: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  /** false once deactivated; missing = active (utils/activeUsers). */
  is_active?: boolean;
}

interface Counts {
  total: number; incoming: number; outgoing: number;
  answered: number; missed: number; with_recording: number;
}

/**
 * The date picker gives a plain 'YYYY-MM-DD' with no timezone, read as the
 * viewer's local day. Stored `started_at` is UTC, so the boundary has to be
 * converted or a filter typed in IST would silently be applied as UTC and be
 * 5½ hours off — the kind of error that looks like missing data, not a bug.
 */
const localDayStartToUtcIso = (value: string): string | undefined => {
  if (!value) return undefined;
  const dt = new Date(`${value}T00:00:00`);
  if (isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
};

const localDayEndToUtcIso = (value: string): string | undefined => {
  if (!value) return undefined;
  const dt = new Date(`${value}T23:59:59.999`);
  if (isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
};

const fmtDateDisplay = (v: string) => {
  if (!v) return '';
  const [year, month, day] = v.split('-');
  if (year && month && day) return `${day}-${month}-${year}`;
  return v;
};

const fmtDateTime = (v?: string) => {
  if (!v) return '—';
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return v;
  return formatDateTimeDdmmyyyy(dt);
};

const fmtSeconds = (n?: number) => {
  const s = Math.max(0, Math.round(n || 0));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
};

/**
 * The call log, under Marketing.
 *
 * Admin/SuperAdmin see every call ever logged; a Marketing Manager/Lead sees
 * their subtree, an Executive their own. Filters are date-range (to the second)
 * and per-employee, which is what a reviewer actually asks of this screen.
 */
const CallsTab: React.FC = () => {
  const { adminMobile } = useAppSelector((s: RootState) => s.auth);
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [callerMobile, setCallerMobile] = useState('');
  const [callType, setCallType] = useState('');
  const [answered, setAnswered] = useState('');
  const [hasRecording, setHasRecording] = useState('');

  // Marketing staff list — same source used by OrdersTab for Onboarded By list
  const [marketingStaff, setMarketingStaff] = useState<MarketingStaff[]>([]);

  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    if (!adminMobile) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        from_date: localDayStartToUtcIso(fromDate),
        to_date: localDayEndToUtcIso(toDate),
        caller_mobile: callerMobile || undefined,
        call_type: callType || undefined,
        answered: answered === 'true' ? true : answered === 'false' ? false : undefined,
        has_recording: hasRecording === 'true' ? true : hasRecording === 'false' ? false : undefined,
        page,
        page_size: pageSize,
      };
      const res = await marketingService.listCalls(adminMobile, params);
      if (res?.statuscode === 200) {
        const fetchedCalls: CallRow[] = res.calls || [];
        setCalls(fetchedCalls);
        setCounts(res.counts || null);
        let resolvedTotal = res?.total ?? res?.count ?? res?.total_count ?? (
          answered === 'true' ? res?.counts?.answered :
            answered === 'false' ? res?.counts?.missed :
              hasRecording === 'true' ? res?.counts?.with_recording :
                callType === 'INCOMING' ? res?.counts?.incoming :
                  callType === 'OUTGOING' ? res?.counts?.outgoing :
                    res?.counts?.total
        ) ?? fetchedCalls.length;
        if (fetchedCalls.length > resolvedTotal) {
          resolvedTotal = fetchedCalls.length;
        }
        // The backend already paginates and reports total_pages directly —
        // trust it over any client-side guess, which can otherwise conjure a
        // phantom empty page whenever a page happens to be exactly full.
        setTotalPages(
          typeof res?.total_pages === 'number' && res.total_pages > 0
            ? res.total_pages
            : Math.max(1, Math.ceil(resolvedTotal / pageSize))
        );
      } else {
        setError(res?.detail || res?.message || 'Failed to load calls');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load calls');
    } finally {
      setLoading(false);
    }
  }, [adminMobile, fromDate, toDate, callerMobile, callType, answered, hasRecording, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Load marketing staff list (same API as OrdersTab Onboarded By)
  useEffect(() => {
    if (!adminMobile) return;
    marketingService.listStaff(adminMobile).then((res) => {
      const staffList: MarketingStaff[] = res?.staff || res?.team || (Array.isArray(res) ? res : []);
      setMarketingStaff(staffList);
    }).catch((err) => {
      console.error('Failed to load marketing staff for caller mobile filter:', err);
      setMarketingStaff([]);
    });
  }, [adminMobile]);

  // Deactivated employees stay listed (their past calls are still theirs),
  // each marked Active / Inactive.
  const callerOptions = useMemo(() => {
    const seen = new Set<string>();
    const list = marketingStaff
      .filter((s) => s.mobile && !seen.has(s.mobile) && !!seen.add(s.mobile))
      .sort((a, b) => (personName(a) || a.mobile).localeCompare(personName(b) || b.mobile))
      .map(staffFilterOption);

    return [{ value: '', label: 'All employees' }, ...list];
  }, [marketingStaff]);

  const selectedEmployeeName = useMemo(() => {
    if (!callerMobile) return '';
    const emp = marketingStaff.find((m) => m.mobile === callerMobile);
    if (!emp) return callerMobile;
    const name = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim();
    return name ? `${name} (${emp.mobile})` : emp.mobile;
  }, [callerMobile, marketingStaff]);

  const hasActiveFilters = Boolean(
    fromDate || toDate || callerMobile || callType || answered || hasRecording
  );

  const handleClearAll = () => {
    setFromDate('');
    setToDate('');
    setCallerMobile('');
    setCallType('');
    setAnswered('');
    setHasRecording('');
    setPage(1);
  };

  const displayedCalls = calls.length > pageSize
    ? calls.slice((page - 1) * pageSize, page * pageSize)
    : calls;

  const inputCls = 'h-[42px] pl-3.5 pr-3 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 outline-none focus:border-[#1B4332] transition-all duration-200';
  const selectCls = `${inputCls} font-semibold`;

  return (
    <div className="w-full space-y-6 pb-12 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select
          value={callerMobile}
          onChange={(v) => {
            setCallerMobile(v);
            setPage(1);
          }}
          options={callerOptions}
          renderValue={staffFilterValue(marketingStaff)}
          className={`${selectCls} min-w-[12rem]`}
        />

        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
          <DateRangePicker
            startDate={fromDate}
            endDate={toDate}
            onChange={(start, end) => { setFromDate(start); setToDate(end); setPage(1); }}
            onClear={() => { setFromDate(''); setToDate(''); setPage(1); }}
            placeholder="Filter by call date..."
          />
          <Select
            value={callType}
            onChange={(v) => {
              setCallType(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All directions' },
              { value: 'INCOMING', label: 'Incoming' },
              { value: 'OUTGOING', label: 'Outgoing' },
            ]}
            className={selectCls}
          />
          <Select
            value={answered}
            onChange={(v) => {
              setAnswered(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All status' },
              { value: 'true', label: 'Answered' },
              { value: 'false', label: 'Missed' },
            ]}
            className={selectCls}
          />
          <Select
            value={hasRecording}
            onChange={(v) => {
              setHasRecording(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All recordings' },
              { value: 'true', label: 'With recording' },
              { value: 'false', label: 'No recording' },
            ]}
            className={selectCls}
          />
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            title="Refresh"
            className={`flex items-center justify-center gap-2 shrink-0 h-[42px] px-4 text-slate-700 border border-slate-200 shadow-sm rounded-xl bg-white outline-none hover:bg-slate-50 transition-all duration-200 cursor-pointer text-sm font-semibold ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin text-[#1B4332]' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Active Filter Chips (like Orders tab) */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 items-center px-1">
          {callerMobile && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">Employee:</span>
              <span className="font-bold">{selectedEmployeeName}</span>
              <button
                type="button"
                onClick={() => { setCallerMobile(''); setPage(1); }}
                className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors cursor-pointer"
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </div>
          )}
          {(fromDate || toDate) && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <Calendar size={11} className="opacity-70 shrink-0" />
              <span className="font-medium opacity-80">Dates:</span>
              <span className="font-bold">{fromDate ? fmtDateDisplay(fromDate) : 'Any'} - {toDate ? fmtDateDisplay(toDate) : 'Any'}</span>
              <button
                type="button"
                onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}
                className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors cursor-pointer"
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </div>
          )}
          {callType && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">Direction:</span>
              <span className="font-bold">{callType === 'INCOMING' ? 'Incoming' : 'Outgoing'}</span>
              <button
                type="button"
                onClick={() => { setCallType(''); setPage(1); }}
                className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors cursor-pointer"
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </div>
          )}
          {answered && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">Status:</span>
              <span className="font-bold">{answered === 'true' ? 'Answered' : 'Missed'}</span>
              <button
                type="button"
                onClick={() => { setAnswered(''); setPage(1); }}
                className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors cursor-pointer"
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </div>
          )}
          {hasRecording && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">Recording:</span>
              <span className="font-bold">{hasRecording === 'true' ? 'With Recording' : 'No Recording'}</span>
              <button
                type="button"
                onClick={() => { setHasRecording(''); setPage(1); }}
                className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors cursor-pointer"
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={handleClearAll}
            className="text-[11px] font-bold text-slate-400 hover:text-slate-700 underline decoration-slate-300 underline-offset-2 transition-colors ml-2 bg-transparent border-none cursor-pointer"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Counts Cards Grid */}
      {counts && (
        <StatCardRow className="mb-0">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-col gap-1 shadow-sm">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Total Calls</p>
            <p className="text-xl sm:text-2xl font-black text-slate-800 mt-1 tracking-tight whitespace-nowrap">{counts.total.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-col gap-1 shadow-sm">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Incoming</p>
            <p className="text-xl sm:text-2xl font-black text-sky-700 mt-1 tracking-tight whitespace-nowrap">{counts.incoming.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-col gap-1 shadow-sm">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Outgoing</p>
            <p className="text-xl sm:text-2xl font-black text-indigo-700 mt-1 tracking-tight whitespace-nowrap">{counts.outgoing.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-col gap-1 shadow-sm">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Answered</p>
            <p className="text-xl sm:text-2xl font-black text-emerald-700 mt-1 tracking-tight whitespace-nowrap">{counts.answered.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-col gap-1 shadow-sm">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Missed</p>
            <p className="text-xl sm:text-2xl font-black text-rose-600 mt-1 tracking-tight whitespace-nowrap">{counts.missed.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-col gap-1 shadow-sm">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Recorded</p>
            <p className="text-xl sm:text-2xl font-black text-amber-700 mt-1 tracking-tight whitespace-nowrap">{counts.with_recording.toLocaleString('en-IN')}</p>
          </div>
        </StatCardRow>
      )}

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-100 bg-slate-50/60">
                {['S.No', 'Started At', 'Employee', 'Contact', 'Lead', 'Direction', 'Duration', 'Connected', 'Status', 'Rec.'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableSkeleton rows={10} cols={10} />
            </tbody>
          </table>
        </div>
      )}
      {!loading && error && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-rose-500 font-medium">
          {error}
        </div>
      )}
      {!loading && !error && calls.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-slate-400 font-medium">
          No calls match these filters.
        </div>
      )}

      {!loading && !error && calls.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-100">
                {['S.No', 'Started At', 'Employee', 'Contact', 'Lead', 'Direction', 'Duration', 'Connected', 'Status', 'Rec.'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayedCalls.map((c, i) => (
                <tr key={c.call_id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="px-4 py-4 text-slate-500 font-medium text-[13px] whitespace-nowrap">{(page - 1) * pageSize + i + 1}</td>
                  <td className="px-4 py-4 text-slate-700 font-medium text-[12px] whitespace-nowrap">{fmtDateTime(c.started_at)}</td>
                  <td className="px-4 py-4">
                    <div className="font-bold text-slate-800 text-[13px] whitespace-nowrap">{c.caller_name || '—'}</div>
                    <MaskedMobile mobile={c.caller_mobile} className="text-slate-500 font-medium text-[12px]" />
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-slate-700 text-[13px] whitespace-nowrap">{c.contact_name || '—'}</div>
                    <MaskedMobile mobile={c.phone_number} className="text-slate-500 font-medium text-[12px]" />
                  </td>
                  <td className="px-4 py-4 text-[12px] whitespace-nowrap">
                    {/* A call whose lead was deleted is still a call. It used to
                        be dropped from this list entirely. */}
                    {c.lead_id
                      ? <><span className="font-mono text-slate-700">{c.lead_id}</span>
                        <div className="text-slate-500">{c.lead_name || ''}</div></>
                      : <span className="text-slate-300 italic">no lead</span>}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold uppercase ${c.call_type === 'INCOMING' ? 'bg-sky-50 text-sky-700' : 'bg-indigo-50 text-indigo-700'}`}>
                      {c.call_type === 'INCOMING' ? <PhoneIncoming size={10} /> : <PhoneOutgoing size={10} />}
                      {c.call_type}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[13px] whitespace-nowrap">{fmtSeconds(c.duration_seconds)}</td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[13px] whitespace-nowrap">{fmtSeconds(c.connected_seconds)}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex px-2 py-1 rounded-lg text-[10px] font-bold uppercase ${c.answered ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                      {c.answered ? 'Answered' : 'Missed'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {c.has_recording
                      ? (c.recording_url
                        ? <button
                            type="button"
                            onClick={() => setPlayingUrl(c.recording_url)}
                            className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 font-bold text-[11px] cursor-pointer bg-transparent border-none p-0"
                          >
                          <Mic size={12} /> Play
                        </button>
                        : <Mic size={13} className="text-amber-600" />)
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages || 1}
        onPageChange={(page) => {
          setPage(page);
          document.getElementById('dashboard-main-scroll-container')?.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {playingUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-5 w-full max-w-sm relative animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setPlayingUrl(null)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors bg-transparent border-none cursor-pointer"
            >
              <X size={16} />
            </button>
            <div className="mb-4 pr-6">
              <h3 className="text-lg font-bold text-slate-800">Call Recording</h3>
              <p className="text-sm text-slate-500">Listen to the recorded call</p>
            </div>
            <audio controls autoPlay src={playingUrl} className="w-full h-10 outline-none" controlsList="nodownload" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CallsTab;
